/* Scoped campaign interactions. Uses the theme's existing Embla distribution. */
(() => {
  window.rtnuFeastCampaign?.destroy();
  const initialized = new Map();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // In-page links from the entry card: scroll to the target and move focus there, so keyboard users land where they scrolled.
  function onShopClick(event) {
    const href = event.currentTarget.getAttribute('href') || '';
    if (href.charAt(0) !== '#' || href.length < 2) return;
    let target = null;
    try { target = document.getElementById(decodeURIComponent(href.slice(1))); } catch (error) { target = null; }
    if (!target) return;
    event.preventDefault();
    if (typeof target.scrollIntoView === 'function') target.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }

  function initPhotoCarousel(root) {
    const viewport = root.querySelector('.embla__viewport');
    const container = root.querySelector('.embla__container');
    if (!viewport || !container) return () => {};
    const slides = Array.from(container.children);
    const autoScrollEnabled = root.dataset.autoScroll !== 'false';
    const pauseOnHover = root.dataset.pauseOnHover === 'true';
    const configuredSpeed = Number.parseFloat(root.dataset.scrollSpeed);
    const speed = Number.isFinite(configuredSpeed) ? Math.min(3, Math.max(0.5, configuredSpeed)) : 1;
    let embla;
    let autoScroll;
    let hovered = false;
    let interacting = false;
    let resizeFrame = 0;
    let lastSize = '';

    const syncMotion = () => {
      if (!autoScroll) return;
      const focused = viewport.contains(document.activeElement);
      const paused = reducedMotion.matches || document.hidden || interacting || focused || (pauseOnHover && hovered);
      if (paused) autoScroll.stop();
      else autoScroll.play(0);
    };
    const clearCarousel = () => {
      embla?.destroy();
      embla = undefined;
      autoScroll = undefined;
      interacting = false;
      root.classList.remove('is-ready');
      container.querySelectorAll('[data-feast-photo-clone]').forEach(clone => clone.remove());
    };
    const build = () => {
      clearCarousel();
      if (!autoScrollEnabled || reducedMotion.matches || slides.length < 2 ||
          typeof window.EmblaCarousel !== 'function' || typeof window.EmblaCarouselAutoScroll !== 'function') return;
      const viewportWidth = viewport.getBoundingClientRect().width;
      const widths = slides.map(slide => slide.getBoundingClientRect().width);
      if (!viewportWidth || widths.some(width => !width)) return;
      const gap = Number.parseFloat(window.getComputedStyle(container).columnGap) || 0;
      const setWidth = widths.reduce((total, width) => total + width + gap, 0);
      const requiredWidth = viewportWidth + Math.max(...widths) + gap;
      // Embla needs enough track to loop. Repeat only the sets needed for wide screens.
      const repeatCount = Math.ceil(requiredWidth / setWidth);
      if (repeatCount > 20) return;
      for (let repeat = 1; repeat < repeatCount; repeat += 1) {
        slides.forEach(slide => {
          const clone = slide.cloneNode(true);
          [clone, ...clone.querySelectorAll('*')].forEach(node => {
            node.removeAttribute('id');
            Array.from(node.attributes).forEach(attribute => {
              if (attribute.name.startsWith('data-shopify')) node.removeAttribute(attribute.name);
            });
          });
          clone.setAttribute('data-feast-photo-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('inert', '');
          clone.querySelectorAll('img').forEach(image => { image.loading = 'eager'; });
          container.append(clone);
        });
      }
      viewport.scrollLeft = 0;
      autoScroll = window.EmblaCarouselAutoScroll({
        speed,
        direction: root.dataset.scrollDirection === 'backward' ? 'backward' : 'forward',
        startDelay: 0,
        playOnInit: false,
        // Coordinate restarts with hover, focus and reduced-motion preferences.
        stopOnInteraction: true,
        stopOnMouseEnter: false,
        stopOnFocusIn: false,
      });
      embla = window.EmblaCarousel(viewport, {
        align: 'start', loop: true, dragFree: true, containScroll: false,
        watchResize: false, watchSlides: false,
      }, [autoScroll]);
      if (!embla.internalEngine().options.loop) { clearCarousel(); return; }
      embla.on('pointerDown', onPointerDown).on('pointerUp', onPointerUp);
      root.classList.add('is-ready');
      syncMotion();
    };
    const onPointerDown = () => { interacting = true; syncMotion(); };
    const onPointerUp = () => { interacting = false; syncMotion(); };
    const onMouseEnter = () => { hovered = true; syncMotion(); };
    const onMouseLeave = () => { hovered = false; syncMotion(); };
    const onFocusOut = event => { if (!viewport.contains(event.relatedTarget)) queueMicrotask(syncMotion); };
    const onKey = event => {
      if (!embla || event.target !== viewport) return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        (event.key === 'ArrowRight' ? embla.scrollNext : embla.scrollPrev)();
      }
    };
    const onResize = () => {
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => {
        const size = [viewport, ...slides].map(node => Math.round(node.getBoundingClientRect().width * 100)).join(',');
        if (size === lastSize) return;
        lastSize = size;
        build();
      });
    };
    const onMotion = () => build();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(onResize) : null;
    observer?.observe(viewport);
    slides.forEach(slide => observer?.observe(slide));
    window.addEventListener('resize', onResize);
    root.addEventListener('mouseenter', onMouseEnter);
    root.addEventListener('mouseleave', onMouseLeave);
    viewport.addEventListener('focusin', syncMotion);
    viewport.addEventListener('focusout', onFocusOut);
    viewport.addEventListener('keydown', onKey);
    document.addEventListener('visibilitychange', syncMotion);
    reducedMotion.addEventListener('change', onMotion);
    lastSize = [viewport, ...slides].map(node => Math.round(node.getBoundingClientRect().width * 100)).join(',');
    build();
    return () => {
      cancelAnimationFrame(resizeFrame);
      observer?.disconnect();
      window.removeEventListener('resize', onResize);
      root.removeEventListener('mouseenter', onMouseEnter);
      root.removeEventListener('mouseleave', onMouseLeave);
      viewport.removeEventListener('focusin', syncMotion);
      viewport.removeEventListener('focusout', onFocusOut);
      viewport.removeEventListener('keydown', onKey);
      document.removeEventListener('visibilitychange', syncMotion);
      reducedMotion.removeEventListener('change', onMotion);
      clearCarousel();
    };
  }

  function initCarousel(root) {
    if (root.dataset.feastCarousel === 'photos') return initPhotoCarousel(root);
    const viewport = root.querySelector('.embla__viewport');
    if (!viewport) return () => {};
    // No cleanup yet: init() retries once Embla has loaded.
    if (typeof window.EmblaCarousel !== 'function') return null;
    // Sections can keep their static row to a wider query via data-static-media; products use the 1024px default.
    const staticMedia = root.dataset.staticMedia || '(min-width: 1024px)';
    // Switch off the native-scroll fallback before Embla measures slide edges and the end gap.
    root.classList.add('is-ready');
    viewport.scrollLeft = 0;
    const embla = window.EmblaCarousel(viewport, {
      align: 'center',
      loop: false,
      dragFree: false,
      containScroll: 'trimSnaps',
      duration: reducedMotion.matches ? 0 : 25,
      breakpoints: { [staticMedia]: { active: false } },
    });
    const prev = root.querySelector('[data-feast-prev]');
    const next = root.querySelector('[data-feast-next]');
    const dots = root.querySelector('[data-feast-dots]');
    let dotButtons = [];
    const update = () => {
      if (prev) { prev.hidden = false; prev.disabled = !embla.canScrollPrev(); }
      if (next) { next.hidden = false; next.disabled = !embla.canScrollNext(); }
      dotButtons.forEach((button, i) => button.setAttribute('aria-current', String(i === embla.selectedScrollSnap())));
    };
    const buildDots = () => {
      if (!dots) return;
      dots.replaceChildren();
      dotButtons = embla.scrollSnapList().map((_, i) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('aria-label', `Show ${root.dataset.feastCarousel === 'prizes' ? 'prize' : 'product'} ${i + 1}`);
        button.addEventListener('click', () => embla.scrollTo(i, reducedMotion.matches));
        if (dots.tagName === 'UL' || dots.tagName === 'OL') {
          const item = document.createElement('li');
          item.append(button);
          dots.append(item);
        } else dots.append(button);
        return button;
      });
    };
    const onPrev = () => embla.scrollPrev(reducedMotion.matches);
    const onNext = () => embla.scrollNext(reducedMotion.matches);
    const onKey = event => {
      if (event.target !== viewport) return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        (event.key === 'ArrowRight' ? onNext : onPrev)();
      }
    };
    const onBlockSelect = event => {
      const selectedSlide = event.target.closest('.embla__slide');
      const slideIndex = embla.slideNodes().indexOf(selectedSlide);
      if (slideIndex < 0) return;
      const snapIndex = embla.internalEngine().slideRegistry.findIndex(slides => slides.includes(slideIndex));
      if (snapIndex >= 0) embla.scrollTo(snapIndex, true);
    };
    const onReinit = () => { buildDots(); update(); };
    const onMotion = () => embla.reInit({ duration: reducedMotion.matches ? 0 : 25 });
    prev?.addEventListener('click', onPrev);
    next?.addEventListener('click', onNext);
    viewport.addEventListener('keydown', onKey);
    root.addEventListener('shopify:block:select', onBlockSelect);
    reducedMotion.addEventListener('change', onMotion);
    embla.on('select', update).on('reInit', onReinit);
    buildDots();
    update();
    return () => {
      prev?.removeEventListener('click', onPrev);
      next?.removeEventListener('click', onNext);
      viewport.removeEventListener('keydown', onKey);
      root.removeEventListener('shopify:block:select', onBlockSelect);
      reducedMotion.removeEventListener('change', onMotion);
      embla.destroy();
      root.classList.remove('is-ready');
      dots?.replaceChildren();
    };
  }

  /* Countdown: data-countdown-state is running, ended or invalid. The values list is aria-hidden, so only the
     status line speaks, and only when the countdown ends. Fires a bubbling feast:countdown-ended on a live end. */
  const COUNTDOWN_UNITS = ['days', 'hours', 'minutes', 'seconds'];
  // An end date needs an explicit offset so every visitor counts down to the same moment.
  const parseCountdownEnd = value => {
    const text = String(value || '').trim();
    return /(?:Z|[+-]\d{2}:\d{2})$/i.test(text) ? Date.parse(text) : NaN;
  };
  // Rounded up: the last second reads 1 and the countdown ends exactly at the end time, like the entry form.
  const countdownRemaining = (endMs, now = Date.now()) => (Number.isFinite(endMs) ? Math.max(0, Math.ceil((endMs - now) / 1000)) : 0);
  const splitCountdown = totalSeconds => {
    const total = Math.max(0, Math.floor(Number(totalSeconds) || 0));
    return { days: Math.floor(total / 86400), hours: Math.floor(total / 3600) % 24, minutes: Math.floor(total / 60) % 60, seconds: total % 60 };
  };
  const formatCountdownValue = (value, pad) => (pad ? String(value).padStart(2, '0') : String(value));

  function initCountdown(root) {
    const end = parseCountdownEnd(root.dataset.endDate);
    const values = root.querySelector('[data-feast-countdown-values]');
    const status = root.querySelector('[data-feast-countdown-status]');
    const nodes = COUNTDOWN_UNITS.map(unit => [unit, root.querySelector(`[data-feast-${unit}]`)]).filter(([, node]) => node);
    // data-pad="false" writes plain integers. Without it, the legacy two-digit output stays.
    const pad = root.dataset.pad !== 'false';
    let state = '';
    let tickTimer = 0;
    let announceTimer = 0;

    const stop = () => { window.clearTimeout(tickTimer); tickTimer = 0; };
    const setState = (next, live = false) => {
      if (state === next) return;
      state = next;
      root.dataset.countdownState = next;
      if (values) values.hidden = next !== 'running';
      if (!status) return;
      if (next === 'running') { status.hidden = true; return; }
      const text = next === 'invalid' ? 'Entry dates will be announced soon.' : root.dataset.endedText || 'This giveaway has ended.';
      if (!live) {
        if (status.textContent !== text) status.textContent = text;
        status.hidden = false;
        return;
      }
      // A live region announces only content added after it is shown, so show it empty and fill it a beat later.
      status.textContent = '';
      status.hidden = false;
      announceTimer = window.setTimeout(() => { announceTimer = 0; status.textContent = text; }, 100);
    };
    const tick = () => {
      stop();
      const now = Date.now();
      const remaining = countdownRemaining(end, now);
      if (!remaining) {
        const live = state === 'running';
        document.removeEventListener('visibilitychange', onVisibility);
        setState(Number.isFinite(end) ? 'ended' : 'invalid', live);
        if (live) root.dispatchEvent(new CustomEvent('feast:countdown-ended', { bubbles: true, detail: { endDate: root.dataset.endDate } }));
        return;
      }
      const units = splitCountdown(remaining);
      nodes.forEach(([unit, node]) => {
        const text = formatCountdownValue(units[unit], pad);
        if (node.textContent !== text) node.textContent = text;
      });
      setState('running');
      // Background tabs throttle timers, so stay idle while hidden and resync on return.
      if (document.hidden) return;
      // Wake where the remaining time crosses a whole second: the next wall-clock second for a whole-second end.
      tickTimer = window.setTimeout(tick, (end - now) % 1000 || 1000);
    };
    const onVisibility = () => {
      if (document.hidden) stop();
      else tick();
    };

    tick();
    if (state === 'running') document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      window.clearTimeout(announceTimer);
      announceTimer = 0;
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }

  /*
    Entry form: one free entry per person per Central Time day, recorded as a Klaviyo client event, with an optional
    email subscription and an optional SMS step.
    Steps: form (1) -> sms (2, only on this browser's first live entry, when the Klaviyo phone embed has rendered) ->
    entered for today (3). A new Central Time day brings the form back.
  */
  const ENTRY = {
    endpoint: 'https://a.klaviyo.com/client/',
    revision: '2026-07-15',
    metric: 'Feast 2026 Activity',
    eventType: 'registration',
    entryMethod: 'free_form',
    // Klaviyo records only the first event per unique_id, profile and metric, so the CT date suffix allows one a day.
    uniqueIdPrefix: 'feast-2026-registration',
    timeZone: 'America/Chicago',
    customSource: 'Feast for a Year 2026',
    defaultCloseAt: '2026-11-15T23:59:59-06:00',
    enteredKey: 'hs_feast_2026_entered',
    attrKey: 'hs_feast_2026_attr',
    smsDoneKey: 'hs_feast_2026_sms_done',
    smsSeenKey: 'hs_feast_2026_sms_seen',
    dayCheckMs: 60 * 1000,
    attrTtlMs: 30 * 24 * 60 * 60 * 1000,
    minSubmitMs: 2500,
    eventAttempts: 3,
    subscriptionAttempts: 2,
    backoffMs: 1000,
    retryAfterCapMs: 5000,
    requestTimeoutMs: 10000,
    smsWaitMs: 1500,
    mockLatencyMs: 700,
  };
  const ATTR_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'ref', 'creative'];
  const ATTR_PROFILE_KEYS = {
    utm_source: 'feast_2026_utm_source', utm_medium: 'feast_2026_utm_medium', utm_campaign: 'feast_2026_utm_campaign',
    utm_content: 'feast_2026_utm_content', utm_term: 'feast_2026_utm_term', ref: 'feast_2026_referrer', creative: 'feast_2026_creative',
  };
  const OPTIN_MODES = ['none', 'checkbox', 'after_entry', 'always'];
  const MOCK_MODES = ['ok', 'error', '429', 'sub_error'];
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)*\.(?:[A-Za-z]{2,}|xn--[A-Za-z0-9-]+)$/;
  const landingHref = window.location.href;
  const entryMock = (() => {
    try {
      const mode = new URLSearchParams(window.location.search).get('feast_entry_mock');
      return MOCK_MODES.includes(mode) ? mode : '';
    } catch (error) { return ''; }
  })();
  // QA only, and only alongside an entry mock: ?feast_sms_mock=1 stands in for the Klaviyo phone embed (see injectSmsMock).
  const smsMock = (() => {
    if (!entryMock) return false;
    try { return new URLSearchParams(window.location.search).get('feast_sms_mock') === '1'; } catch (error) { return false; }
  })();
  const entryRoots = new Map();

  // Storage can be blocked (private mode, ITP, disabled cookies). Every access goes through these.
  const store = {
    get(key) { try { return window.localStorage.getItem(key); } catch (error) { return null; } },
    set(key, value) { try { window.localStorage.setItem(key, value); return true; } catch (error) { return false; } },
    remove(key) { try { window.localStorage.removeItem(key); } catch (error) { /* storage blocked */ } },
  };
  const readJson = key => {
    const raw = store.get(key);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (error) { return null; }
  };
  const cleanText = (value, max = 200) => String(value == null ? '' : value).trim().slice(0, max);

  function isValidEmail(value) {
    const email = cleanText(value, 320);
    return email.length <= 254 && EMAIL_PATTERN.test(email);
  }
  function parseCloseAt(value) {
    const text = cleanText(value, 40);
    const parsed = /(?:Z|[+-]\d{2}:\d{2})$/i.test(text) ? Date.parse(text) : NaN;
    if (Number.isFinite(parsed)) return parsed;
    if (text) window.console.warn(`[feast entry] close_at "${text}" needs an ISO date with offset. Using ${ENTRY.defaultCloseAt}.`);
    return Date.parse(ENTRY.defaultCloseAt);
  }
  const isClosed = (closeAtMs, now = Date.now()) => Number.isFinite(closeAtMs) && now > closeAtMs;
  // The entry day is the Central Time calendar date, 'YYYY-MM-DD'. Intl applies the CDT to CST change on Nov 1.
  const ctFormat = (() => {
    try {
      return new Intl.DateTimeFormat('en-US', { timeZone: ENTRY.timeZone, year: 'numeric', month: '2-digit', day: '2-digit' });
    } catch (error) { return null; }
  })();
  function ctDate(now = Date.now()) {
    const date = new Date(now);
    if (Number.isNaN(date.getTime())) return '';
    if (ctFormat) {
      const parts = {};
      ctFormat.formatToParts(date).forEach(part => { parts[part.type] = part.value; });
      return `${parts.year}-${parts.month}-${parts.day}`;
    }
    // No time zone support: Central Standard Time (UTC-6) all year.
    return new Date(date.getTime() - 6 * 60 * 60 * 1000).toISOString().slice(0, 10);
  }
  // What a page load or a day check shows. Only an entry stored with today's CT date counts as entered; a flag from an
  // earlier day, or the old one-entry format without last_entry_date, brings back the form.
  function entryView(flag, closeAtMs, now = Date.now()) {
    if (isClosed(closeAtMs, now)) return 'closed';
    return flag && typeof flag === 'object' && flag.last_entry_date === ctDate(now) ? 'entered' : 'form';
  }
  // The close date's own CT day, when step 3 swaps "come back tomorrow" for the last-day message.
  const isLastDay = (closeAtMs, now = Date.now()) =>
    Number.isFinite(closeAtMs) && !isClosed(closeAtMs, now) && ctDate(now) === ctDate(closeAtMs);
  function guardReason(honeypotValue, initAt, now = Date.now()) {
    if (cleanText(honeypotValue)) return 'honeypot';
    if (now - initAt < ENTRY.minSubmitMs) return 'too_fast';
    return '';
  }
  function optInPlan(mode, checked, listId) {
    const requested = mode === 'always' || (mode === 'checkbox' && checked === true);
    const hasList = cleanText(listId) !== '';
    return {
      requested,
      subscribe: requested && hasList,
      offerAfter: mode === 'after_entry' && hasList,
      warnMissingList: (requested || mode === 'after_entry') && !hasList,
    };
  }

  // First touch: keep the earliest unexpired visit that carried campaign parameters.
  function readAttribution(now = Date.now()) {
    const record = readJson(ENTRY.attrKey);
    if (!record || typeof record !== 'object') return null;
    if (!(Number(record.expires_at) > now)) { store.remove(ENTRY.attrKey); return null; }
    return record;
  }
  function captureAttribution(href = window.location.href, now = Date.now()) {
    let params;
    try { params = new URL(href).searchParams; } catch (error) { return readAttribution(now); }
    const touch = {};
    let hasParams = false;
    ATTR_PARAMS.forEach(name => {
      touch[name] = cleanText(params.get(name));
      if (touch[name]) hasParams = true;
    });
    const existing = readAttribution(now);
    if (existing || !hasParams) return existing;
    const record = Object.assign(touch, {
      landing_url: cleanText(href, 2000),
      captured_at: new Date(now).toISOString(),
      expires_at: now + ENTRY.attrTtlMs,
    });
    store.set(ENTRY.attrKey, JSON.stringify(record));
    return record;
  }
  const pageAttribution = captureAttribution();
  const currentAttribution = () => readAttribution() || pageAttribution || null;

  function buildEventPayload(input) {
    const attribution = input.attribution || {};
    const value = name => cleanText(attribution[name]);
    const surface = cleanText(input.entrySurface) || 'landing_hero';
    const optIn = input.emailOptIn === true;
    const enteredAt = cleanText(input.enteredAt) || new Date(Date.now()).toISOString();
    // The CT day this entry claims. The ledger counts days by Klaviyo's server event time; this is the browser's view.
    const givenDate = cleanText(input.entryDate);
    const entryDate = (/^\d{4}-\d{2}-\d{2}$/.test(givenDate) && givenDate) || ctDate(Date.parse(enteredAt)) || ctDate();
    const profileProperties = {
      feast_2026_entrant: true,
      // Most recent form entry.
      feast_2026_entered_at: enteredAt,
      feast_2026_last_entry_date: entryDate,
      feast_2026_entry_source: surface,
    };
    // Blank attribution is left off the profile so a later visit without UTMs cannot overwrite earlier values.
    ATTR_PARAMS.forEach(name => { if (value(name)) profileProperties[ATTR_PROFILE_KEYS[name]] = value(name); });
    profileProperties.feast_2026_email_opt_in = optIn;
    return {
      data: {
        type: 'event',
        attributes: {
          properties: {
            type: ENTRY.eventType,
            entry_date: entryDate,
            entry_method: ENTRY.entryMethod,
            entry_surface: surface,
            page_url: cleanText(input.pageUrl, 2000),
            landing_url: cleanText(input.landingUrl || attribution.landing_url, 2000),
            email_opt_in: optIn,
            utm_source: value('utm_source'),
            utm_medium: value('utm_medium'),
            utm_campaign: value('utm_campaign'),
            utm_content: value('utm_content'),
            utm_term: value('utm_term'),
            referring_partner: value('ref'),
            creative: value('creative'),
            form_version: cleanText(input.formVersion),
          },
          metric: { data: { type: 'metric', attributes: { name: ENTRY.metric } } },
          profile: { data: { type: 'profile', attributes: { email: cleanText(input.email, 320), properties: profileProperties } } },
          unique_id: `${ENTRY.uniqueIdPrefix}-${entryDate}`,
        },
      },
    };
  }
  function buildSubscriptionPayload(input) {
    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: ENTRY.customSource,
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: cleanText(input.email, 320),
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
              },
            },
          },
        },
        relationships: { list: { data: { type: 'list', id: cleanText(input.listId) } } },
      },
    };
  }

  // Retry policy: 202 succeeds; network errors (status 0), 429 and 5xx retry; anything else fails.
  function parseRetryAfter(header, now = Date.now()) {
    if (header == null || String(header).trim() === '') return null;
    const text = String(header).trim();
    const ms = /^\d+(?:\.\d+)?$/.test(text) ? Number(text) * 1000 : Date.parse(text) - now;
    return Number.isFinite(ms) ? Math.min(ENTRY.retryAfterCapMs, Math.max(0, Math.round(ms))) : null;
  }
  function classifyResult(result, attempt, maxAttempts) {
    if (result.status === 202) return 'success';
    const retryable = result.status === 0 || result.status === 429 || (result.status >= 500 && result.status <= 599);
    return retryable && attempt < maxAttempts ? 'retry' : 'fail';
  }
  function retryDelayMs(attempt, retryAfter, random = Math.random) {
    const hinted = parseRetryAfter(retryAfter);
    if (hinted !== null) return hinted;
    return ENTRY.backoffMs * Math.pow(2, attempt - 1) + Math.round(random() * 250);
  }
  const sleep = ms => new Promise(resolve => { window.setTimeout(resolve, ms); });

  // QA only: ?feast_entry_mock=ok|error|429|sub_error simulates Klaviyo with no network calls.
  // 429 answers the first two event attempts with Retry-After: 1, then accepts.
  async function mockSend(kind, body, attempt) {
    window.console.info(`[feast entry] mock "${entryMock}": POST /client/${kind}/ attempt ${attempt}`, body);
    await sleep(ENTRY.mockLatencyMs);
    if (kind === 'events' && entryMock === 'error') return { status: 400, retryAfter: null };
    if (kind === 'events' && entryMock === '429' && attempt < 3) return { status: 429, retryAfter: '1' };
    if (kind === 'subscriptions' && entryMock === 'sub_error') return { status: 500, retryAfter: null };
    return { status: 202, retryAfter: null };
  }
  // QA only (?feast_entry_mock=<mode>&feast_sms_mock=1): a static stand-in for the Klaviyo phone embed, so step 2 can be
  // checked before the real form exists. It copies Klaviyo's markup shape and inline styles so the theme overrides are
  // exercised. It sends nothing and never reads the field; its button swaps in a success view and fires the same
  // klaviyoForms submit event as the real embed.
  const SMS_MOCK_FONT = 'font-family:Arial,Helvetica,sans-serif;';
  const smsMockRow = content => `<div data-testid="form-row" style="display:flex;padding:0 0 10px"><div data-testid="form-component" style="flex:1 1 0%;padding:0 6px">${content}</div></div>`;
  function injectSmsMock(target, formId) {
    target.innerHTML = `<form class="needsclick klaviyo-form" data-feast-sms-mock novalidate style="display:flex;flex-direction:column;padding:10px;background-color:rgb(255,255,255);${SMS_MOCK_FONT}">${
      smsMockRow(`<input type="tel" name="phone_number" placeholder="Phone number" aria-label="Phone number" autocomplete="tel" style="box-sizing:border-box;width:100%;height:38px;padding:0 0 0 16px;border:1px solid rgb(155,155,155);border-radius:2px;background-color:rgb(255,255,255);color:rgb(0,0,0);${SMS_MOCK_FONT}font-size:16px;font-weight:400">`)}${
      smsMockRow(`<p><span style="${SMS_MOCK_FONT}font-size:10px;color:rgb(110,110,110)">QA mock disclosure. By signing up, you agree to receive recurring automated marketing texts from Heart &amp; Soil at this number. Consent is not a condition of purchase. Msg and data rates may apply. Reply STOP to cancel. <a href="#" style="color:rgb(110,110,110)">Terms</a> and <a href="#" style="color:rgb(110,110,110)">Privacy</a>.</span></p>`)}${
      smsMockRow(`<button type="submit" style="width:100%;height:44px;padding:10px 0;border:0;border-radius:0;background:rgb(0,0,0);color:rgb(255,255,255);${SMS_MOCK_FONT}font-size:16px;font-weight:700;letter-spacing:0">Get texts</button>`)
    }</form>`;
    const mockForm = target.querySelector('form');
    mockForm.addEventListener('submit', event => {
      event.preventDefault();
      mockForm.innerHTML = smsMockRow(`<p><span style="${SMS_MOCK_FONT}font-size:18px;color:rgb(0,0,0)">Almost done. Reply Y to the text we just sent to confirm.</span></p>`);
      window.console.info(`[feast entry] mock: klaviyoForms submit for SMS form ${formId}`);
      window.dispatchEvent(new CustomEvent('klaviyoForms', { detail: { type: 'submit', formId, metaData: {} } }));
    });
  }
  async function sendOnce(kind, companyId, body, attempt) {
    if (entryMock) return mockSend(kind, body, attempt);
    const url = `${ENTRY.endpoint}${kind}/?company_id=${encodeURIComponent(companyId)}`;
    const controller = typeof window.AbortController === 'function' ? new window.AbortController() : null;
    const timer = controller ? window.setTimeout(() => controller.abort(), ENTRY.requestTimeoutMs) : 0;
    try {
      // Content-Type and revision are the two headers Klaviyo's CORS preflight allows. No cookies are sent.
      const response = await window.fetch(url, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json', revision: ENTRY.revision },
        body: JSON.stringify(body),
        signal: controller ? controller.signal : undefined,
      });
      // Retry-After is only readable if Klaviyo exposes it over CORS; null falls back to backoff.
      return { status: response.status, retryAfter: response.headers.get('Retry-After') };
    } catch (error) {
      return { status: 0, retryAfter: null };
    } finally {
      if (timer) window.clearTimeout(timer);
    }
  }
  async function sendWithRetry(kind, companyId, body, maxAttempts) {
    for (let attempt = 1; ; attempt += 1) {
      const result = await sendOnce(kind, companyId, body, attempt);
      const step = classifyResult(result, attempt, maxAttempts);
      if (step !== 'retry') return { ok: step === 'success', status: result.status, attempts: attempt };
      await sleep(retryDelayMs(attempt, result.retryAfter));
    }
  }
  async function subscribeEmail(config, email) {
    if (!config.listId) {
      window.console.warn('[feast entry] Configuration error: email opt-in requested but klaviyo_list_id is blank. No subscription sent.');
      return false;
    }
    let result;
    try {
      result = await sendWithRetry('subscriptions', config.companyId, buildSubscriptionPayload({ email, listId: config.listId }), ENTRY.subscriptionAttempts);
    } catch (error) { result = { ok: false, status: 0 }; }
    if (!result.ok) window.console.warn(`[feast entry] Email subscription failed (status ${result.status}). The entry was still recorded.`);
    return result.ok;
  }
  function identifyKlaviyo(email) {
    if (entryMock) { window.console.info('[feast entry] mock: skipped klaviyo.identify'); return; }
    const klaviyo = window.klaviyo;
    if (!klaviyo || typeof klaviyo.identify !== 'function') return;
    try {
      const pending = klaviyo.identify({ email });
      if (pending && typeof pending.catch === 'function') pending.catch(() => {});
    } catch (error) { /* klaviyo.js failure must not block the entered state */ }
  }
  function readEntryConfig(root) {
    const data = root.dataset;
    const mode = cleanText(data.optinMode);
    return {
      companyId: cleanText(data.companyId),
      listId: cleanText(data.listId),
      optinMode: OPTIN_MODES.includes(mode) ? mode : 'none',
      closeAt: parseCloseAt(data.closeAt),
      smsEnabled: data.smsEnabled === 'true',
      smsFormId: cleanText(data.smsFormId).replace(/[^\w-]/g, ''),
      surface: cleanText(data.surface) || 'landing_hero',
      formVersion: cleanText(data.formVersion),
      errorMessage: cleanText(data.errorMessage, 500) || 'Something went wrong. Please try again.',
      invalidMessage: cleanText(data.invalidMessage, 500) || 'Please enter a valid email address.',
    };
  }

  function initEntry(root) {
    const form = root.querySelector('[data-feast-entry-form]');
    const input = root.querySelector('[data-feast-email]');
    if (!form || !input) return { config: readEntryConfig(root), reset() {}, onSmsSubmit: () => false, cleanup() {} };
    const optin = root.querySelector('[data-feast-optin]');
    const honeypot = root.querySelector('[data-feast-hp]');
    const submit = root.querySelector('[data-feast-submit]');
    const errorNode = root.querySelector('[data-feast-error]');
    const formHeading = root.querySelector('[data-feast-form-heading]');
    const entered = root.querySelector('[data-feast-entered]');
    const heading = root.querySelector('[data-feast-entered-heading]');
    const enteredBody = root.querySelector('[data-feast-entered-body]');
    const enteredBodyLastDay = root.querySelector('[data-feast-entered-body-last-day]');
    const closed = root.querySelector('[data-feast-closed]');
    const optinAfter = root.querySelector('[data-feast-optin-after]');
    const optinAfterDone = root.querySelector('[data-feast-optin-after-done]');
    const shopLink = root.querySelector('[data-feast-shop-link]');
    const resetButton = root.querySelector('[data-feast-reset]');
    const config = readEntryConfig(root);
    const sms = root.querySelector('[data-feast-sms]');
    const smsHeading = root.querySelector('[data-feast-sms-heading]');
    const smsSkip = root.querySelector('[data-feast-sms-skip]');
    const smsTarget = sms && config.smsEnabled && config.smsFormId ? sms.querySelector(`.klaviyo-form-${config.smsFormId}`) : null;
    const smsLabels = {
      skip: cleanText(smsSkip && smsSkip.dataset.skipLabel) || 'No thanks',
      continue: cleanText(smsSkip && smsSkip.dataset.continueLabel) || 'Continue',
    };
    const initAt = Date.now();
    let email = '';
    let inFlight = false;
    let optinBusy = false;
    let optinDone = false;
    // Set once Klaviyo reports a submit. Its success view then fills the embed for the rest of the page view (the QA
    // mock is re-rendered on reset instead).
    let smsSubmitted = false;
    let stopSmsWatch = () => {};
    // Step 2 is offered on this browser's first entry only. The stored flag covers later days; this covers blocked storage.
    let smsSeen = false;
    const smsAlreadySeen = () => smsSeen || Boolean(store.get(ENTRY.smsSeenKey) || store.get(ENTRY.smsDoneKey));
    // The CT date of the entry step 3 is showing. A tab left open past CT midnight returns to the form: checked when the
    // tab becomes visible again and once a minute while visible. Only step 3 is checked, so typing is never interrupted.
    let shownDate = '';
    let dayTimer = 0;
    const stopDayWatch = () => {
      if (dayTimer) window.clearInterval(dayTimer);
      dayTimer = 0;
    };

    if (!config.companyId && !entryMock) window.console.warn('[feast entry] Configuration error: data-company-id is blank.');
    if (errorNode && !errorNode.id && input.id) errorNode.id = `${input.id}-error`;

    const showError = (message, invalidField) => {
      if (errorNode) {
        errorNode.textContent = message;
        errorNode.hidden = !message;
      }
      const describedBy = (input.getAttribute('aria-describedby') || '').split(/\s+/).filter(token => token && token !== (errorNode && errorNode.id));
      if (invalidField) {
        input.setAttribute('aria-invalid', 'true');
        if (errorNode && errorNode.id) describedBy.push(errorNode.id);
      } else input.removeAttribute('aria-invalid');
      if (describedBy.length) input.setAttribute('aria-describedby', describedBy.join(' '));
      else input.removeAttribute('aria-describedby');
    };
    const setState = state => {
      root.dataset.state = state;
      if (state !== 'entered') stopDayWatch();
      form.hidden = state === 'sms' || state === 'entered' || state === 'closed';
      if (sms) sms.hidden = state !== 'sms';
      if (entered) entered.hidden = state !== 'entered';
      if (closed) closed.hidden = state !== 'closed';
      const busy = state === 'submitting';
      form.setAttribute('aria-busy', String(busy));
      input.readOnly = busy;
      if (submit) {
        ['aria-disabled', 'aria-busy'].forEach(name => {
          if (busy) submit.setAttribute(name, 'true');
          else submit.removeAttribute(name);
        });
      }
      if (state !== 'error') showError('', false);
    };

    const focusHeading = (node, options) => {
      if (!node) return;
      if (!node.hasAttribute('tabindex')) node.setAttribute('tabindex', '-1');
      node.focus(options);
    };

    // Step 2 needs Klaviyo's rendered phone form. Resolves true once it is there, false after smsWaitMs so a slow,
    // blocked or untargeted form goes straight to step 3, and null when reset or teardown cancels the wait.
    const embedRendered = node => node.querySelector('form, input, button, iframe') !== null || node.textContent.trim() !== '';
    const waitForSmsEmbed = () => new Promise(resolve => {
      stopSmsWatch();
      if (!smsTarget || smsSubmitted) { resolve(false); return; }
      if (embedRendered(smsTarget)) { resolve(true); return; }
      let observer = null;
      let poll = 0;
      let timeout = 0;
      const finish = ready => {
        if (observer) observer.disconnect();
        window.clearInterval(poll);
        window.clearTimeout(timeout);
        stopSmsWatch = () => {};
        resolve(ready);
      };
      const check = () => { if (embedRendered(smsTarget)) finish(true); };
      stopSmsWatch = () => finish(null);
      if (typeof window.MutationObserver === 'function') {
        observer = new window.MutationObserver(check);
        observer.observe(smsTarget, { childList: true, subtree: true, characterData: true });
      } else poll = window.setInterval(check, 100);
      timeout = window.setTimeout(() => finish(false), ENTRY.smsWaitMs);
    });

    // Step 2 is the tallest step. Its height becomes the floor for the rest of the flow, so Klaviyo's shorter success
    // view and step 3 don't shrink the card again. Reset releases it.
    const holdPanels = [sms, entered].filter(Boolean);
    const holdSmsHeight = () => {
      const height = Math.ceil(sms.getBoundingClientRect().height);
      holdPanels.forEach(node => { node.style.minHeight = `${height}px`; });
    };
    const releaseSmsHeight = () => holdPanels.forEach(node => node.style.removeProperty('min-height'));
    const showSms = () => {
      smsSeen = true;
      store.set(ENTRY.smsSeenKey, JSON.stringify({ shown_at: new Date(Date.now()).toISOString() }));
      setState('sms');
      if (smsSkip) smsSkip.textContent = smsLabels.skip;
      holdSmsHeight();
      focusHeading(smsHeading);
    };
    const showEntered = ({ focus, live, date }) => {
      shownDate = date || ctDate();
      setState('entered');
      // The close date's CT day swaps "come back tomorrow" for the last-day message, when one is set.
      if (enteredBodyLastDay) {
        const lastDay = isLastDay(config.closeAt);
        enteredBodyLastDay.hidden = !lastDay;
        if (enteredBody) enteredBody.hidden = lastDay;
      }
      if (optinAfter) optinAfter.hidden = !(live && email && !optinDone && optInPlan(config.optinMode, false, config.listId).offerAfter);
      if (optinAfterDone) optinAfterDone.hidden = !optinDone;
      if (!dayTimer) dayTimer = window.setInterval(checkDay, ENTRY.dayCheckMs);
      if (focus) focusHeading(heading);
    };
    const completeEntry = async ({ optIn, enteredAt, entryDate }) => {
      identifyKlaviyo(email);
      shownDate = entryDate;
      // No email is stored. A new CT day shows the form again, and browser autofill supplies the address.
      store.set(ENTRY.enteredKey, JSON.stringify({ last_entry_date: entryDate, entered_at: enteredAt }));
      document.documentElement.dataset.feastEntered = 'true';
      document.dispatchEvent(new CustomEvent('feast:entered', {
        detail: { surface: config.surface, emailOptIn: optIn, formVersion: config.formVersion },
      }));
      // The busy button stays up while the wait runs, so the card never shows a gap.
      const smsReady = smsAlreadySeen() ? false : await waitForSmsEmbed();
      if (smsReady === null) return;
      if (smsReady) showSms();
      else showEntered({ focus: true, live: true, date: entryDate });
    };
    // Called by the klaviyoForms listener. The label change is the cue; Klaviyo's own success view stays in the embed.
    const onSmsSubmit = formId => {
      if (!smsTarget || formId !== config.smsFormId) return false;
      smsSubmitted = true;
      if (smsSkip) smsSkip.textContent = smsLabels.continue;
      return true;
    };

    const onSubmit = async event => {
      event.preventDefault();
      if (inFlight) return;
      if (isClosed(config.closeAt)) { setState('closed'); return; }
      const value = cleanText(input.value, 320);
      if (!isValidEmail(value)) {
        setState('error');
        showError(config.invalidMessage, true);
        input.focus();
        return;
      }
      const plan = optInPlan(config.optinMode, Boolean(optin && optin.checked), config.listId);
      const guard = guardReason(honeypot ? honeypot.value : '', initAt);
      // A filled honeypot means a bot: show success, send and store nothing.
      if (guard === 'honeypot') {
        email = '';
        showEntered({ focus: true, live: false, date: ctDate() });
        return;
      }
      inFlight = true;
      setState('submitting');
      // Autofill lets a real person submit inside the minimum time, so wait out the rest instead of dropping the entry.
      if (guard === 'too_fast') await sleep(Math.max(0, initAt + ENTRY.minSubmitMs - Date.now()));
      const attribution = currentAttribution();
      const sentAt = Date.now();
      const enteredAt = new Date(sentAt).toISOString();
      const entryDate = ctDate(sentAt);
      const payload = buildEventPayload({
        email: value,
        emailOptIn: plan.requested,
        entrySurface: config.surface,
        formVersion: config.formVersion,
        attribution,
        pageUrl: window.location.href,
        landingUrl: (attribution && attribution.landing_url) || landingHref,
        enteredAt,
        entryDate,
      });
      let result;
      try { result = await sendWithRetry('events', config.companyId, payload, ENTRY.eventAttempts); } catch (error) { result = { ok: false }; }
      if (!result.ok) {
        inFlight = false;
        setState('error');
        showError(config.errorMessage, false);
        return;
      }
      email = value;
      // Runs in the background; a blank list ID only logs a configuration warning.
      if (plan.requested) subscribeEmail(config, value);
      await completeEntry({ optIn: plan.requested, enteredAt, entryDate });
      inFlight = false;
    };
    const onInput = () => { if (root.dataset.state === 'error') setState('form'); };
    const onOptinAfter = async () => {
      if (optinBusy || !email || optinDone) return;
      optinBusy = true;
      optinAfter.setAttribute('aria-disabled', 'true');
      optinAfter.setAttribute('aria-busy', 'true');
      const ok = await subscribeEmail(config, email);
      optinBusy = false;
      optinAfter.removeAttribute('aria-disabled');
      optinAfter.removeAttribute('aria-busy');
      if (!ok) return;
      optinDone = true;
      optinAfter.hidden = true;
      if (optinAfterDone) {
        optinAfterDone.hidden = false;
        if (!optinAfterDone.hasAttribute('tabindex')) optinAfterDone.setAttribute('tabindex', '-1');
        optinAfterDone.focus();
      }
    };
    // "Not you?" and the day rollover: back to step 1 so another person, or the same person on a new day, can enter.
    const reset = ({ focus = true } = {}) => {
      stopSmsWatch();
      store.remove(ENTRY.enteredKey);
      delete document.documentElement.dataset.feastEntered;
      shownDate = '';
      email = '';
      optinDone = false;
      input.value = '';
      if (optin) optin.checked = false;
      if (optinAfterDone) optinAfterDone.hidden = true;
      if (smsSkip) smsSkip.textContent = smsLabels.skip;
      releaseSmsHeight();
      if (smsMock && smsTarget) {
        injectSmsMock(smsTarget, config.smsFormId);
        smsSubmitted = false;
      }
      if (isClosed(config.closeAt)) { setState('closed'); return; }
      setState('form');
      if (focus) input.focus();
    };
    const checkDay = () => {
      if (root.dataset.state !== 'entered' || document.hidden) return;
      const now = Date.now();
      if (!isClosed(config.closeAt, now) && ctDate(now) === shownDate) return;
      const view = entryView(readJson(ENTRY.enteredKey), config.closeAt, now);
      // Another tab may already have entered for the new day.
      if (view === 'entered') { showEntered({ focus: false, live: false, date: ctDate(now) }); return; }
      const hadFocus = root.contains(document.activeElement);
      reset({ focus: false });
      // Keep keyboard and screen reader users in the card without opening the on-screen keyboard.
      if (hadFocus && view === 'form') focusHeading(formHeading, { preventScroll: true });
    };
    const onVisibility = () => { if (!document.hidden) checkDay(); };
    const onReset = () => reset();
    const onSmsSkip = () => showEntered({ focus: true, live: true, date: shownDate });
    form.addEventListener('submit', onSubmit);
    input.addEventListener('input', onInput);
    optinAfter?.addEventListener('click', onOptinAfter);
    resetButton?.addEventListener('click', onReset);
    shopLink?.addEventListener('click', onShopClick);
    smsSkip?.addEventListener('click', onSmsSkip);
    document.addEventListener('visibilitychange', onVisibility);

    if (config.optinMode === 'after_entry' && !config.listId) {
      window.console.warn('[feast entry] Configuration error: optin_mode is after_entry but klaviyo_list_id is blank. The opt-in button stays hidden.');
    }
    if (smsMock && smsTarget) injectSmsMock(smsTarget, config.smsFormId);
    // The theme editor keeps showing the form even after a test entry in the same browser.
    const designMode = Boolean(window.Shopify && window.Shopify.designMode);
    const storedFlag = readJson(ENTRY.enteredKey);
    const initialView = entryView(storedFlag, config.closeAt);
    if (initialView === 'closed') setState('closed');
    else if (!designMode && initialView === 'entered') {
      // Entered today in this browser: the confirmation only. The SMS step follows a live entry.
      document.documentElement.dataset.feastEntered = 'true';
      showEntered({ focus: false, live: false, date: storedFlag.last_entry_date });
    } else setState('form');

    return {
      config,
      reset,
      onSmsSubmit,
      cleanup() {
        stopSmsWatch();
        stopDayWatch();
        document.removeEventListener('visibilitychange', onVisibility);
        form.removeEventListener('submit', onSubmit);
        input.removeEventListener('input', onInput);
        optinAfter?.removeEventListener('click', onOptinAfter);
        resetButton?.removeEventListener('click', onReset);
        shopLink?.removeEventListener('click', onShopClick);
        smsSkip?.removeEventListener('click', onSmsSkip);
        entryRoots.delete(root);
      },
    };
  }
  function initEntries(scope = document) {
    scope.querySelectorAll('[data-feast-entry]').forEach(root => {
      if (initialized.has(root)) return;
      const api = initEntry(root);
      entryRoots.set(root, api);
      initialized.set(root, api.cleanup);
    });
  }
  function resetEntries(root) {
    entryRoots.forEach((api, entryRoot) => { if (!root || root === entryRoot) api.reset({ focus: false }); });
  }
  // Native Klaviyo embed submits (the SMS step): the matching card switches its skip button to Continue, and a dated
  // record is kept. The phone number in the event is never read or stored.
  const onKlaviyoForms = event => {
    const detail = event && event.detail;
    if (!detail || detail.type !== 'submit') return;
    const formId = String(detail.formId || '');
    let matched = false;
    entryRoots.forEach(api => { if (api.onSmsSubmit(formId)) matched = true; });
    if (matched) store.set(ENTRY.smsDoneKey, JSON.stringify({ submitted_at: new Date().toISOString(), form_id: formId }));
  };
  window.addEventListener('klaviyoForms', onKlaviyoForms);

  // ViralSweep entry (snippets/feast-viralsweep-entry.liquid). Its loader sizes the widget iframe by postMessage. The first
  // size message, or an iframe that already has a height, marks the card ready and drops the reserved height. If neither
  // happens in time (a content blocker, a failed load), the fallback note shows; a late size message still recovers.
  const VIRALSWEEP_ORIGIN = 'https://app.viralsweep.com';
  const VIRALSWEEP_TIMEOUT_MS = 12000;
  // Central Time date of this browser's last ViralSweep entry. Only the date is kept, never the email the widget sends.
  const VIRALSWEEP_ENTERED_KEY = 'hs_feast_2026_vs_entered';
  function initViralsweep(root) {
    const shopLink = root.querySelector('[data-feast-shop-link]');
    shopLink?.addEventListener('click', onShopClick);
    const fallback = root.querySelector('[data-feast-viralsweep-fallback]');
    if (!fallback) return () => shopLink?.removeEventListener('click', onShopClick);
    const promotionId = root.dataset.promotionId || '';
    const frame = () => root.querySelector('iframe.vs_main_frame');
    const setState = state => {
      root.dataset.state = state;
      fallback.hidden = state !== 'failed';
    };
    // Entered today: the bonus button becomes the card's main action. ViralSweep only messages a fresh entry, not a
    // returning entrant's page load, so the CT date stored on entry carries it through reloads for the rest of the day.
    const syncEntered = () => {
      if (store.get(VIRALSWEEP_ENTERED_KEY) === ctDate()) root.dataset.entered = 'true';
      else delete root.dataset.entered;
    };
    const onMessage = event => {
      if (event.origin !== VIRALSWEEP_ORIGIN) return;
      if (event.source !== frame()?.contentWindow) return;
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      if (data.entry === 'true' && String(data.id) === promotionId) {
        store.set(VIRALSWEEP_ENTERED_KEY, ctDate());
        root.dataset.entered = 'true';
      }
      if (!('height' in data)) return;
      window.clearTimeout(timer);
      setState('ready');
    };
    const onVisibility = () => { if (!document.hidden) syncEntered(); };
    const timer = window.setTimeout(() => {
      if (root.dataset.state === 'loading') setState(frame()?.offsetHeight > 0 ? 'ready' : 'failed');
    }, VIRALSWEEP_TIMEOUT_MS);
    window.addEventListener('message', onMessage);
    document.addEventListener('visibilitychange', onVisibility);
    syncEntered();
    if (frame()?.offsetHeight > 0) setState('ready');
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      document.removeEventListener('visibilitychange', onVisibility);
      shopLink?.removeEventListener('click', onShopClick);
    };
  }

  let emblaRetryTimer = 0;
  let emblaRetries = 0;
  function init(scope = document) {
    let waitingForEmbla = false;
    scope.querySelectorAll('[data-feast-carousel], [data-feast-countdown]').forEach(root => {
      if (initialized.has(root)) return;
      const cleanup = root.hasAttribute('data-feast-carousel') ? initCarousel(root) : initCountdown(root);
      if (cleanup) initialized.set(root, cleanup);
      else waitingForEmbla = true;
    });
    initEntries(scope);
    scope.querySelectorAll('[data-feast-viralsweep]').forEach(root => {
      if (!initialized.has(root)) initialized.set(root, initViralsweep(root));
    });
    // Cookiebot can re-insert theme scripts as async, so Embla may load after this file. Retry for up to 10s.
    if (waitingForEmbla && !emblaRetryTimer && emblaRetries < 40) {
      emblaRetries += 1;
      emblaRetryTimer = window.setTimeout(() => { emblaRetryTimer = 0; init(); }, 250);
    }
  }
  function destroy(scope) {
    initialized.forEach((cleanup, root) => {
      if (scope.contains(root)) { cleanup(); initialized.delete(root); }
    });
  }
  const onReady = () => init();
  const onSectionLoad = event => init(event.target);
  const onSectionUnload = event => destroy(event.target);
  const onFaqSelect = event => {
    const details = event.target.closest('#feast-main .ot-faq details');
    if (details) details.open = true;
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', onReady, { once: true });
  else init();
  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('shopify:section:unload', onSectionUnload);
  document.addEventListener('shopify:block:select', onFaqSelect);
  window.rtnuFeastCampaign = {
    destroy() {
      document.removeEventListener('DOMContentLoaded', onReady);
      document.removeEventListener('shopify:section:load', onSectionLoad);
      document.removeEventListener('shopify:section:unload', onSectionUnload);
      document.removeEventListener('shopify:block:select', onFaqSelect);
      window.removeEventListener('klaviyoForms', onKlaviyoForms);
      window.clearTimeout(emblaRetryTimer);
      initialized.forEach(cleanup => cleanup());
      initialized.clear();
      entryRoots.clear();
    },
    entry: {
      init: scope => initEntries(scope || document),
      reset: resetEntries,
      buildEventPayload,
      buildSubscriptionPayload,
      // Pure decision helpers, exposed for debugging and the test harness.
      helpers: {
        isValidEmail, parseCloseAt, isClosed, ctDate, entryView, isLastDay, guardReason, optInPlan, readAttribution,
        captureAttribution, parseRetryAfter, classifyResult, retryDelayMs, mock: entryMock, smsMock, constants: ENTRY,
      },
    },
    countdown: {
      // Pure countdown helpers, exposed for debugging and the test harness.
      helpers: { parseCountdownEnd, countdownRemaining, splitCountdown, formatCountdownValue },
    },
  };
})();
